// Putting a finished picture on the clipboard.
//
// The people these tools are for paste into Discord more often than they
// attach a file, so a copy button sits next to every "Save image" on the tools
// that produce one picture. It is a convenience on top of saving, never a
// replacement, and on a browser that cannot write images to the clipboard it
// is simply not shown.

/** Whether this browser can put an image on the clipboard at all. */
export function canCopyImages() {
    return Boolean(
        window.isSecureContext
        && navigator.clipboard
        && typeof navigator.clipboard.write === 'function'
        && typeof window.ClipboardItem === 'function'
    );
}

/**
 * The picture as PNG, the one image type every clipboard takes. A JPEG or WebP
 * result is drawn once more and re-encoded; nothing about it changes but the
 * container, and the saved file keeps the format that was picked.
 */
async function asPng(blob) {
    if (blob.type === 'image/png') return blob;
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0);
    bitmap.close();
    return new Promise((resolve, reject) => {
        canvas.toBlob(png => (png ? resolve(png) : reject(new Error('Could not prepare the image'))), 'image/png');
    });
}

function tell(message, kind) {
    if (window.ui && typeof window.ui.toast === 'function') window.ui.toast(message, kind);
}

/**
 * Wire a copy button.
 *
 * @param {object}   options
 * @param {Element}  options.button   The copy button, rendered hidden.
 * @param {Element}  [options.mirror] A button whose disabled state this one
 *                                    follows, normally the tool's Save button.
 * @param {Function} options.collect  Returns, or resolves to, `{ blob }` for
 *                                    the current result, or nothing.
 */
export function wireCopy({ button, mirror, collect }) {
    if (!button || !canCopyImages()) return;
    button.hidden = false;

    if (mirror) {
        const follow = () => { button.disabled = mirror.disabled; };
        follow();
        new MutationObserver(follow).observe(mirror, { attributes: true, attributeFilter: ['disabled'] });
    }

    const label = button.querySelector('[data-copy-label]');
    const idle = label ? label.textContent : '';

    button.addEventListener('click', async () => {
        try {
            // The item is handed a promise rather than a finished blob: the
            // write has to start inside the click, and rendering a large
            // result can take longer than a browser keeps that window open.
            const png = Promise.resolve(collect()).then(result => {
                if (!result || !result.blob) throw new Error('Nothing to copy yet');
                return asPng(result.blob);
            });
            await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': png })]);
            if (label) {
                label.textContent = 'Copied';
                setTimeout(() => { label.textContent = idle; }, 1600);
            }
            tell('Image copied. Paste it wherever you need it.', 'success');
        } catch (err) {
            tell('The image could not be copied here. Use Save image instead.', 'error');
        }
    });
}
